import {
    FloatingList,
    FloatingPortal,
    autoUpdate,
    flip,
    offset,
    size,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useListNavigation,
    useRole
} from '@floating-ui/react';
import React, { ReactNode, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { FaXmark } from 'react-icons/fa6';

import { SingleOption } from './select-types';

export interface SelectContextValue {
    getItemProps: ReturnType<typeof useInteractions>['getItemProps'];
    handleSelect: (option: SingleOption<any>) => void;
}
export const SelectContext = createContext<SelectContextValue>({} as SelectContextValue);

const SCROLL_TOLLERANCE = 4;
const ID = 'select-input';

export interface SelectProps<T> {
    id?: string;
    placeholder?: string;
    onChange?: (value: string) => void;
    onSelect?: (option: SingleOption<T> | null) => void;
    onScrollToBottom?: () => void;
    value?: string;
    isClearable?: boolean;
    children: ReactNode;
    optionsLoading?: boolean;
    onFocus?: React.FocusEventHandler<HTMLInputElement> | undefined;
    onBlur?: React.FocusEventHandler<HTMLInputElement> | undefined;
    disabled?: boolean;
    isOpenOverwrite?: boolean;
    inputRefCallback?: (ref: React.RefObject<HTMLInputElement | null>) => void;
}

export function Select<T>(props: SelectProps<T>) {
    const inputRef = useRef<HTMLInputElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);

    useEffect(() => {
        if (props.isOpenOverwrite !== undefined) {
            setIsOpen(props.isOpenOverwrite);
        }
    }, [props.isOpenOverwrite]);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: (open, event, reason) => {
            if (props.isOpenOverwrite === undefined) {
                setIsOpen(open);
            }
        },
        placement: 'bottom-start',
        whileElementsMounted: autoUpdate,
        middleware: [
            offset(5),
            flip({ padding: 10 }),
            size({
                apply({ rects, elements }) {
                    Object.assign(elements.floating.style, {
                        width: `${rects.reference.width}px`
                    });
                },
                padding: 10
            })
        ]
    });

    const elementsRef = useRef<Array<HTMLElement | null>>([]);
    const labelsRef = useRef<Array<string | null>>([]);

    const handleSelect = useCallback(
        (option: SingleOption<T>) => {
            props.onSelect && props.onSelect(option);
            setIsOpen(false);
            inputRef.current?.focus();
        },
        [props.onSelect]
    );

    const listNav = useListNavigation(context, {
        listRef: elementsRef,
        activeIndex,
        onNavigate: setActiveIndex,
        allowEscape: true
    });

    const click = useClick(context, {
        event: 'mousedown',
        toggle: true,
        enabled: !props.disabled
    });

    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'listbox' });

    const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([listNav, click, dismiss, role]);

    const selectContext = useMemo(
        (): SelectContextValue => ({
            getItemProps,
            handleSelect
        }),
        [getItemProps, handleSelect]
    );

    useEffect(() => {
        const handleListScroll = () => {
            if (listRef.current) {
                if (listRef.current.scrollTop + listRef.current.clientHeight >= listRef.current.scrollHeight - SCROLL_TOLLERANCE) {
                    props.onScrollToBottom && props.onScrollToBottom();
                }
            }
        };
        const currentListRef = listRef.current;
        currentListRef?.addEventListener('scroll', handleListScroll);
        return () => {
            currentListRef?.removeEventListener('scroll', handleListScroll);
        };
    }, [isOpen, props.onScrollToBottom]);

    return (
        <div>
            <div
                className='input-group'
                ref={refs.setReference}
                {...getReferenceProps()}>
                <input
                    data-testid={props.id}
                    ref={inputRef}
                    id={props.id || ID + '-search-input'}
                    className='form-control'
                    type='text'
                    value={props.value ?? ''}
                    onChange={(e) => props.onChange && props.onChange(e.target.value)}
                    placeholder={props.placeholder || 'Search...'}
                    onFocus={props.onFocus}
                    onBlur={props.onBlur}
                    disabled={props.disabled}
                    autoComplete='off'
                />
                {props.isClearable && props.value && (
                    <span
                        className='input-group-text'
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation();
                            props.onChange && props.onChange('');
                            props.onSelect && props.onSelect(null);
                            inputRef.current?.focus();
                        }}
                        id={props.id || ID + '-clear-button'}>
                        <FaXmark />
                    </span>
                )}
                <span
                    className='input-group-text'
                    aria-disabled={props.disabled}
                    id={props.id || ID + '-dropdown-button'}>
                    {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                </span>
            </div>

            <SelectContext.Provider value={selectContext}>
                {isOpen && (
                    <FloatingPortal>
                        <div
                            className='z-index-1051'
                            ref={refs.setFloating}
                            style={floatingStyles}
                            {...getFloatingProps()}>
                            <FloatingList
                                elementsRef={elementsRef}
                                labelsRef={labelsRef}>
                                <ul
                                    ref={listRef}
                                    role='listbox'
                                    className='list-group no-scrollbar mt-1'
                                    style={{ maxHeight: '30vh', overflowY: 'auto' }}>
                                    {props.optionsLoading ? <li className='list-group-item option'>Loading...</li> : props.children}
                                </ul>
                            </FloatingList>
                        </div>
                    </FloatingPortal>
                )}
            </SelectContext.Provider>
        </div>
    );
}
